import type {
  SlrGoodStandingReason,
  SlrGoodStandingState,
  SlrLoanStandingAction,
  SlrLoanStandingAuditState,
  SlrLoanStandingPresentation,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

const GOOD_STANDING_STATES: ReadonlySet<string> = new Set([
  'unknown',
  'eligible',
  'ineligible',
  'undeterminable',
] satisfies SlrGoodStandingState[]);

/**
 * Normalizes a raw wire value (JSON:API read or cable broadcast) to a known
 * good-standing state. A new or malformed backend value must degrade to
 * `unknown` — `deriveSlrLoanStandingStatus` has no default branch, so an
 * unrecognized state would otherwise yield an undefined presentation status
 * and an undefined translation key.
 */
export function normalizeSlrGoodStanding(value: string | null | undefined): SlrGoodStandingState {
  return value && GOOD_STANDING_STATES.has(value) ? (value as SlrGoodStandingState) : 'unknown';
}

const ACTION_TO_CTA_KEY: Partial<Record<SlrLoanStandingAction, string>> = {
  upload_document: 'slr.loan_standing.actions.upload_document',
  upload_newer_document: 'slr.loan_standing.actions.upload_newer_document',
  confirm_name_match: 'slr.loan_standing.actions.confirm_name_match',
  contact_support: 'slr.loan_standing.actions.contact_support',
};

const STATUS_TO_TITLE_KEY: Record<SlrLoanStandingStatus, string> = {
  unknown: 'slr.loan_standing.title.pending_upload',
  pending_upload: 'slr.loan_standing.title.pending_upload',
  processing: 'slr.loan_standing.title.processing',
  eligible: 'slr.loan_standing.title.eligible',
  ineligible: 'slr.loan_standing.title.ineligible',
  try_again: 'slr.loan_standing.title.try_again',
  cannot_determine: 'slr.loan_standing.title.cannot_determine',
};

const STATUS_TO_DESCRIPTION_KEY: Record<SlrLoanStandingStatus, string> = {
  unknown: 'slr.loan_standing.description.pending_upload',
  pending_upload: 'slr.loan_standing.description.pending_upload',
  processing: 'slr.loan_standing.description.processing',
  eligible: 'slr.loan_standing.description.eligible',
  ineligible: 'slr.loan_standing.description.ineligible',
  try_again: 'slr.loan_standing.description.try_again',
  cannot_determine: 'slr.loan_standing.description.cannot_determine',
};

// tio-api `good_standing_reason` values → existing description translation keys.
// Copy is reused as-is (no YAML changes); `doc_too_old` intentionally covers
// both the stale-document and outside-audit-window reasons. `none` and
// `manual_override` produce no alert, so they fall through to the status-based
// description. Source of truth: tio-api EmployeePlanAccount GOOD_STANDING_REASON_*.
const REASON_TO_DESCRIPTION_KEY: Record<string, string> = {
  missing_document: 'slr.loan_standing.description.pending_upload',
  stale_document: 'slr.loan_standing.description.doc_too_old',
  outside_audit_window: 'slr.loan_standing.description.doc_too_old',
  missing_required_observations: 'slr.loan_standing.description.insufficient_observations',
  name_mismatch: 'slr.loan_standing.description.name_mismatch',
  name_match_confirmation_required:
    'slr.loan_standing.description.name_match_confirmation_required',
  status_not_in_good_standing: 'slr.loan_standing.description.not_in_good_standing',
  delinquent_or_collections: 'slr.loan_standing.description.not_in_good_standing',
  defaulted: 'slr.loan_standing.description.in_default',
  delinquent: 'slr.loan_standing.description.delinquent',
  in_collections: 'slr.loan_standing.description.in_collections',
  past_due: 'slr.loan_standing.description.past_due',
  parent_plus_only: 'slr.loan_standing.description.all_parent_plus',
  undeterminable: 'slr.loan_standing.description.cannot_determine',

  // statement_required_for_repayment_status IS a real good_standing_reason as
  // of tio-api#9960 (undeterminable verdict: reject_past_due + NSLDS-only
  // evidence + a loan in repayment). The others below are document-pipeline
  // codes retained pending the #8714/D4 surface confirmation.
  nslds_required_for_federal_non_repayment_status:
    'slr.loan_standing.description.nslds_required_for_federal_non_repayment_status',
  statement_required_for_repayment_status:
    'slr.loan_standing.description.statement_required_for_repayment_status',
  unsupported_document_for_status: 'slr.loan_standing.description.unsupported_document_for_status',
  unsupported_format: 'slr.loan_standing.description.unsupported_format',
  extraction_failed: 'slr.loan_standing.description.extraction_failed',
};

const INTENT_BY_STATUS: Record<SlrLoanStandingStatus, SlrLoanStandingPresentation['intent']> = {
  eligible: 'success',
  processing: 'info',
  unknown: 'warning',
  pending_upload: 'warning',
  ineligible: 'warning',
  try_again: 'warning',
  cannot_determine: 'warning',
};

/**
 * Maps tio-api's raw `good_standing` state (+ `good_standing_reason`) to the
 * frontend presentation status. tio-api owns 4 states; the UI distinguishes a
 * few more (pending-upload vs. try-again vs. cannot-determine). `processing` is
 * a client-only transient status and is never derived here.
 *
 * Source of truth: tio-api EmployeePlanAccount GOOD_STANDING_STATE_* /
 * GOOD_STANDING_REASON_*.
 */
export function deriveSlrLoanStandingStatus(
  goodStanding: SlrGoodStandingState,
  reason?: SlrGoodStandingReason | null
): SlrLoanStandingStatus {
  switch (goodStanding) {
    case 'eligible':
      return 'eligible';
    case 'unknown':
      return 'pending_upload';
    case 'undeterminable':
      return 'cannot_determine';
    case 'ineligible':
      if (reason === 'missing_document') return 'pending_upload';
      if (reason === 'stale_document' || reason === 'outside_audit_window') return 'try_again';
      // A hard name mismatch is a DOCUMENT problem, not a loan verdict: the
      // canonical mockups (Figma "Name matching — doc name doesn't match EF
      // name", 767:16696 flow) keep the upload framing with a mismatch error
      // and a "try another document" path — never the "This Loan Isn't
      // Eligible" dead end (#10017; QA scenario A8).
      if (reason === 'name_mismatch') return 'try_again';
      // Recoverable second-document asks (doc pipeline): the reported status
      // needs an NSLDS file / current statement — the user can act, so this is
      // try_again (upload) rather than hard ineligibility (contact support).
      if (
        reason === 'nslds_required_for_federal_non_repayment_status' ||
        reason === 'statement_required_for_repayment_status'
      ) {
        return 'try_again';
      }
      return 'ineligible';
  }
}

/**
 * Derives the primary CTA action from the presentation status: not-yet-uploaded
 * → upload, recoverable (stale / outside-window / cannot-determine) → re-upload,
 * hard ineligibility → contact support. The per-reason policy can be refined
 * once the upload/observation contract lands (D4); this is a sensible default.
 */
export function deriveSlrLoanStandingAction(
  goodStanding: SlrGoodStandingState,
  reason?: SlrGoodStandingReason | null
): SlrLoanStandingAction {
  // Fuzzy name match (tio-api#8054): the eval parked the EPA undeterminable
  // pending the participant's yes/no answer — surface the confirm CTA rather
  // than another upload ask.
  if (reason === 'name_match_confirmation_required') return 'confirm_name_match';

  switch (deriveSlrLoanStandingStatus(goodStanding, reason)) {
    case 'pending_upload':
      return 'upload_document';
    case 'try_again':
    case 'cannot_determine':
      return 'upload_newer_document';
    case 'ineligible':
      return 'contact_support';
    case 'eligible':
    case 'processing':
    case 'unknown':
      return 'none';
  }
}

export function presentationForSlrLoanStanding(
  state: SlrLoanStandingAuditState
): SlrLoanStandingPresentation {
  const primaryReason = state.primaryReason ?? state.reasons?.[0] ?? state.failures?.[0]?.code;

  return {
    titleKey: STATUS_TO_TITLE_KEY[state.status],
    descriptionKey:
      (primaryReason && REASON_TO_DESCRIPTION_KEY[primaryReason]) ||
      STATUS_TO_DESCRIPTION_KEY[state.status],
    ctaKey: ACTION_TO_CTA_KEY[state.action],
    intent: INTENT_BY_STATUS[state.status],
    requiresAttention:
      state.action !== 'none' && state.status !== 'eligible' && state.status !== 'processing',
  };
}

export function shouldShowSlrLoanStandingAlert(state?: SlrLoanStandingAuditState | null) {
  if (!state?.featureEnabled) return false;
  const presentation = presentationForSlrLoanStanding(state);
  return presentation.requiresAttention || state.status === 'processing';
}

// The good-standing verdict owns the loan-eligibility story for the receiving
// account, so a server alert saying the same thing must not double up. Today
// that's the account-ineligible `invalid-loan-type` alert (Slr::AlertService emits it
// when the receiving account is Parent-PLUS-only) — the verdict already renders
// "This Loan Isn't Eligible" with the Parent PLUS reason and a support path.
//
// The tio-payment validation-error cards this used to filter are gone: they were
// employee-blended and could describe a non-receiving account, so the dashboard
// now takes account messaging solely from /slr-alerts + the verdict.
// Convergence rationale: tio-front-end#9811.
const PARENT_PLUS_OWNED_ALERT_IDS = new Set(['slr-invalid-loan-type']);

export function suppressVerdictOwnedActionItems<T extends { id: string }>(
  items: T[],
  state?: SlrLoanStandingAuditState | null
): T[] {
  if (!state?.featureEnabled) return items;
  if (state.primaryReason !== 'parent_plus_only') return items;
  return items.filter((item) => !PARENT_PLUS_OWNED_ALERT_IDS.has(item.id));
}
